import React, { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { CreditCard, CheckCircle, Zap } from "lucide-react";

const PACKS = [
  { qty: 10, label: "Starter" },
  { qty: 50, label: "Batch", popular: true },
  { qty: 200, label: "Bulk" },
  { qty: 1000, label: "Enterprise" },
];

export default function Billing() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(null);

  const buyReal = async (qty) => {
    if (!window.Razorpay) {
      toast.error("Payment SDK not loaded. Refresh and try again.");
      return;
    }
    setBusy(qty);
    try {
      const { data: order } = await api.post("/credits/create-order", { quantity: qty });
      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "DIGITAL CARD",
        description: `${qty} ID Card Credits`,
        order_id: order.order_id,
        prefill: {
          name: user?.name || "",
          email: user?.email || "",
        },
        theme: { color: "#002FA7" },
        handler: async (response) => {
          try {
            const { data: verify } = await api.post("/credits/verify-payment", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success(`Payment successful! +${verify.quantity_added ?? qty} credits`);
            await refresh();
          } catch (err) {
            toast.error(err.response?.data?.detail || "Verification failed");
          } finally {
            setBusy(null);
          }
        },
        modal: {
          ondismiss: () => {
            setBusy(null);
            toast.info("Payment cancelled");
          },
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        toast.error("Payment failed: " + (resp.error?.description || "unknown"));
        setBusy(null);
      });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create order");
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="billing-page">
      <div>
        <div className="font-mono text-xs uppercase tracking-widest text-[#002FA7] font-bold mb-2">// Credits</div>
        <h1 className="font-display font-black text-4xl tracking-tighter">Buy credits.</h1>
        <p className="text-gray-600 mt-1">₹10 per ID card. Credits never expire. Powered by Razorpay.</p>
      </div>

      <div className="bg-black text-white brutal-border brutal-shadow-lg p-6 flex items-center justify-between">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest opacity-70">Current balance</div>
          <div className="font-display font-black text-5xl mt-1" data-testid="credit-balance">{user?.credits ?? 0} <span className="text-2xl text-gray-400">credits</span></div>
        </div>
        <CreditCard className="w-16 h-16 text-[#22C55E]" strokeWidth={1.5}/>
      </div>

      <div className="bg-black text-white brutal-border p-4 font-mono text-xs uppercase tracking-widest font-bold flex items-center gap-2">
        <Zap className="w-4 h-4 text-[#22C55E]"/> LIVE Payments · Powered by Razorpay · UPI · Cards · Netbanking · Wallets
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        {PACKS.map(p => (
          <div key={p.qty} className={`bg-white brutal-border brutal-shadow p-6 flex flex-col ${p.popular ? "ring-4 ring-[#002FA7] ring-offset-0" : ""}`} data-testid={`pack-${p.qty}`}>
            {p.popular && <div className="bg-[#002FA7] text-white px-2 py-1 font-mono text-[10px] uppercase tracking-widest font-bold mb-3 inline-block w-fit">Popular</div>}
            <div className="font-mono text-xs uppercase tracking-widest text-gray-500">{p.label}</div>
            <div className="font-display font-black text-5xl mt-2">{p.qty}</div>
            <div className="font-mono text-xs text-gray-500 mb-4">credits</div>
            <div className="font-display font-bold text-2xl mb-4">₹{p.qty * 10}</div>
            <ul className="text-xs space-y-1 mb-6 flex-1">
              <li className="flex gap-2"><CheckCircle className="w-3 h-3 text-[#22C55E] mt-0.5"/> {p.qty} HD cards</li>
              <li className="flex gap-2"><CheckCircle className="w-3 h-3 text-[#22C55E] mt-0.5"/> PNG + PDF</li>
              <li className="flex gap-2"><CheckCircle className="w-3 h-3 text-[#22C55E] mt-0.5"/> Never expires</li>
            </ul>
            <button onClick={()=>buyReal(p.qty)} disabled={busy===p.qty} className="w-full bg-[#002FA7] text-white font-bold uppercase tracking-widest text-xs px-4 py-3 brutal-border brutal-shadow brutal-hover disabled:opacity-60" data-testid={`buy-${p.qty}`}>
              {busy===p.qty ? "Processing..." : `Pay ₹${p.qty * 10}`}
            </button>
          </div>
        ))}
      </div>

      <div className="bg-white brutal-border p-4">
        <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">// Accepted payment methods</div>
        <div className="font-mono text-xs text-gray-800 space-y-1">
          <div>✓ UPI (Google Pay, PhonePe, Paytm, BHIM)</div>
          <div>✓ Credit / Debit Cards (Visa, Mastercard, RuPay, Amex)</div>
          <div>✓ Netbanking · 60+ Indian banks</div>
          <div>✓ Wallets (Paytm, PhonePe, Amazon Pay, Freecharge)</div>
          <div className="text-gray-500 mt-2">🔒 Payments secured by Razorpay · 256-bit SSL</div>
        </div>
      </div>
    </div>
  );
}
